/**
 * Interface for the 'Orderform' data
 */
import { AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { UserTrackingState, ProductListState } from '../models/user-tracking.state';

export interface OrderformPrice {
  net: number;
  net_effective?: number;
  gross: number;
  gross_effective?: number;
  vat?: number;
}

export interface OrderformAmountItem {
  first: OrderformPrice;
  oth: OrderformPrice;
}

export interface OrderformProductItemQuant {
  is_change_allowed: boolean;
  is_editable: boolean;
  min_value: number;
  max_value: number;
  input_type: 'input' | 'none' | 'checkbox' | 'radiobutton';
  max_type: string;
  has_discount: boolean;
  pre_quantity?: number; // Renew
}

export interface OrderformProductItem {
  product_id: number;
  type_id: number;
  image_url: string;
  headline: string;
  description: string;
  product_description?: string;
  service_description?: string;
  have_variant: boolean;
  variant?: {
    template?: string;
    options: {
      id: number;
      label: {
        label: string;
        disabled: boolean;
      } & string;
    }[];
  };
  have_eticket: boolean;
  eticket?: {
    input_type: 'select' | 'radiobutton';
    template?: string;
    options: {
      id: number;
      label: string;
      stock: string;
      date: string;
      have_stock: 'Y';
      hint: string;
      country: string;
    }[];
  };
  delivery_type?: string;
  is_optional_if_addons_present: string;
  quantity: OrderformProductItemQuant;
  sales_left?: number; // benötigt?
  product_sales_left?: number; // benötigt?
  unit_amounts?: OrderformAmountItem;
  unit_amounts_undiscounted?: OrderformAmountItem;
  total_amounts?: OrderformAmountItem;
  total_amounts_undiscounted?: OrderformAmountItem;
  shipping_amounts?: OrderformAmountItem;
  have_warning: boolean;
  warning_message?: string;
  product_safety_compliance?: string;
}

export type TypeCountriesList = Array<[string, string] | '-'>;

export interface OrderformProductSettings {
  vat_included_hint: string;
  have_plain_price_view: boolean;
  quantity_input_type: 'input' | 'none' | 'checkbox' | 'radiobutton';
  refund_days: number;
}

export interface OrderformLanguageOptions {
  code: string;
  label: string;
  image_url: string;
  locale: string; // e.g de_DE
}

export interface OrderformCurrencyOption {
  code: string;
  label: string;
}

export interface OrderformGlobalSettings {
  language: string;
  locale: string;
  product_language?: string;
  language_options: OrderformLanguageOptions[];
  currency_code: string;
  currency_symbol: string;
  currency_options: OrderformCurrencyOption[];
  is_show_cent_amount_part_shown: boolean;
  is_search_engine_allowed: boolean;
  is_vat_shown: boolean;
  is_vat_hint_mandatory: boolean;
  is_voucher_input_enabled: boolean;
  is_voucher_input_readonly: boolean;
  payment_legal_hint?: string;
  is_plain_mode_enabled?: boolean;
}

export type BillingDescriptorVariant = 'control' | 'variant_b' | 'variant_c';
export type SubscriptionPriceCopyVariant = 'control' | 'variant_a' | 'variant_b';

export interface OrderformConfig {
  product_id: number;
  orderform_id: number;
  google_api_key: string;
  recaptcha_site_key: string;
  ds_global_growthbook_token: string;
  ds_global_mixpanel_token: string;
  disable_address_autocomplete: boolean;
  hide_street_number: boolean;
  is_street_number_show_first: boolean;
  can_preselect_payplan: boolean;
  is_pay_button_label_changed_allowed: boolean;
  is_pay_button_label_changed_allowed_countries: string[];
  upsell_session_id?: number;
  siteowner_id: number;
  merchant_id?: number | null;
  is_private_siteowner: boolean;
  ajax_key: string;
  buyer_country: string;
  show_order_summary_before_purchase: boolean;
  payment_button_label_overwrite: string | null;
  hide_process_popup?: boolean;
  is_strikethrough_active: boolean;
  new_security_seal_icons: boolean;
  express_checkout?: boolean;
  is_voucher_in_summary?: boolean;
  buy_button_label_variant?: string;
  product_list_new_vertical_layout: boolean;
  product_language_transparency?: string;
  product_language_transparency_message?: string;
  is_reduced_max_width?: boolean;
  billing_descriptor_variant?: BillingDescriptorVariant;
  billing_descriptor_order_number?: boolean;
  billing_descriptor_custom_text?: string;
  subscription_price_copy_due_today?: string;
  subscription_price_copy_then_prefix?: string;
  subscription_price_copy_once?: string;
  upsell_position?: string;
  upsell_number?: number;
}

export interface OrderformExitPopupSettings {
  enabled: boolean;
  content: string;
  max_count: number;
  current_count: number;
  cookie: string;
  domain: string;
}

export interface OrderformFooterMenu {
  url: string;
  label: string;
  type: string;
  id?: string;
}

export interface OrderformSettingsState {
  config: OrderformConfig;
  global: OrderformGlobalSettings;
  affiliate: {
    name: string;
    note: string;
  };
  product: {
    items: OrderformProductItem[];
    settings: OrderformProductSettings;
  };
  contact_form: {
    refund_waiver_checkboxes: any[];
    fields_billing_address: FormFields.Field[];
    fields_shipping_address: FormFields.Field[];
    postname_prefix: string;
    is_phone_no_mandatory: boolean;
    readonly_buyer_postnames: any[];
    has_title: boolean;
    has_address: boolean;
    has_shipping: boolean;
    has_phone_no_on_address_form_only: boolean;
    has_phone_no_always: boolean;
    is_business_buyer_mandatory: boolean;
    is_business_preselected: boolean;
    is_billing_address_form_open: boolean;
    has_business_option: boolean;
    has_company_input: boolean;
    has_tax_id: boolean;
    addr_salutation_mode: 'if_address_is_shown' | 'always_shown' | 'never_shown';
    default_salutation: 'M' | 'F';
  };
  static_texts: {
    footer_menu: OrderformFooterMenu[];
    legal_notes: string[];
    reseller: string;
  };
  exit_popup: OrderformExitPopupSettings;
  custom_forms: OrderformSettingsStateCustomForms.RootObject[];
  checkboxes: FormFields.Field[];
}

export interface OrderformSelectedPlanQuantityDiscount {
  product_index: number;
  product_id: number;
  html: string;
  label: string | null;
  type: 'discount_not_applied' | 'discount_applied';
}

export interface OrderformSelectedPlan {
  payment_plan_id: string;
  interval_unit: string;
  interval_unit_per?: string;
  number_of_installments: number;
  billing_type: string;
  single_price: number;
  original_price: number;
  display_original_price: boolean;
  quantity_discounts: OrderformSelectedPlanQuantityDiscount[];
  rebilling: boolean;
}

export interface OrderformPaymentPlanItem extends OrderformSelectedPlan {
  headline_html: string;
  description_html: string;
  input_style: 'checkbox' | 'radiobutton';
}

export interface OrderformOrderProductItemQuant {
  value: number;
}

export interface OrderformOrderProductItem {
  product_id?: number;
  list_index?: number;
  quantity?: OrderformOrderProductItemQuant;
  eticket_date_id?: number;
  variant_id?: number;
  customForm?: { [formNo: number]: CustomFormData };
}

export interface OrderformOrderOrderState {
  checkboxes: CustomFormData;
  items: { [product_id: number]: OrderformOrderProductItem };
  hidden_inputs?: CustomFormData;
  payment_plan_id: string;
  payment_plan_is_brutto_price_enforced: boolean;
  preselected_payment_plan_id: string;
  selected_plan: OrderformSelectedPlan;
  voucher_code: string;
  currency_code: string;
  currency: string;
  payment_provider: OrderformPaymentProvider;
  custom_forms: CustomFormData;
  discount_display_position: OrderformDiscountDisplayPosition;
  billing_address: CustomFormData;
  shipping_address: CustomFormData;
  splittest_data: string; // to identify spit test product
  has_billing_address: number;
  purchase_id: string;
  confirm_repurchase?: boolean;
}

export interface OrderformOrderStateMessageNotificationAction {
  code: string;
  label: string;
  url: string | null;
  ajax_url?: string;
  primary?: true;
  secondary?: true;
}

export interface OrderformOrderStateMessageNotification {
  type: string;
  message: string;
  code: string;
  actions: OrderformOrderStateMessageNotificationAction[];
}

export interface OrderformOrderStateMessages {
  success: any[];
  infos: any[];
  warnings: any[];
  errors: any[];
  notifications: OrderformOrderStateMessageNotification[];
  errors_by_postname: any[];
  plan_info_tooltip: {
    is_shown: boolean;
    label: string;
    tooltip_headline: string;
    tooltip_contents_url: string;
  };
}

export interface OrderformValidationState {
  voucher_code: string;
  form_validation: string;
  errors_by_postname: {
    [key: string]: string;
  };
  success_by_postname: {
    [key: string]: string;
  };
}

export interface OrderformPaymentPlanChangesState {
  new_payment_plan?: string;
  current_payment_plan: string;
}

export interface OrderformOrderState {
  userTracking: UserTrackingState;
  productList?: ProductListState;

  /**
   * legacy unordered order form state data
   */
  country: string;
  merchant: string;
  settings: OrderformSettingsState;
  payment: OrderformPaymentProvider[];
  payment_plans_headline: string;
  payment_plans: OrderformPaymentPlanItem[];
  order_summary: {
    footnote: string;
    shipping_cost_label: string;
    plan_info_tooltip: {
      is_shown: boolean;
      label: string;
      tooltip_headline: string;
      tooltip_contents_url: string;
    };
    shipping_total: OrderformAmountItem;
    shipping_total_undiscounted: OrderformAmountItem;
    order_total_undiscounted: OrderformAmountItem;
    order_total: OrderformAmountItem;
    order_was_purchased_before: boolean;
    brutto_discounts: { reason_html: string; amount: number }[];
    order_final: {
      is_shown: boolean;
      gross_1st: number;
    };
    vat_amounts: {
      vat_rate: number;
      label: string;
      vat_1st: number;
      vat_oth: number;
    }[];
    payment_plan: {
      billing_type: string;
      number_of_installments: number;
      test_interval: string;
      first_billing_interval: string;
      other_billing_intervals: string;
      first_payment_at: string;
    };
  };
  messages: OrderformOrderStateMessages;
  /**
   * all data that are filled from frontend side,
   * no other place in store should be updated from frontend!
   */
  order: OrderformOrderOrderState;
  requestedOrder?: OrderformOrderOrderState;
  execute: {
    action: string;
  };
  validation: OrderformValidationState;
  payment_plan_changes?: OrderformPaymentPlanChangesState;
  express_checkout?: OrderformPaymentProvider;
  purchase_cause?: string;
  buy_url_id?: string | null;
  cart_id?: string | null;
}

export interface InterfacePaymentProviderStipe {
  apiUrl: string;
  publicKey: string;
  intentClientSecret: string;
}

export interface OrderformPaymentProvider {
  payment_provider_id: string;
  pay_method: string;
  renderer_type: string;
  label_data: {
    default_submit_button: string;
    hint_text: string;
  };
  config_data: any | InterfacePaymentProviderStipe;
  icon_data: {
    text?: string;
    alt: string;
    url: {
      dark: string;
      light: string;
    };
    width: number;
    height: number;
    large?: {
      text?: string;
      url: string;
      width: number;
      height: number;
    };
    small?: {
      text?: string;
      url: string;
      width: number;
      height: number;
    };
  };
  status?: {
    status: 'none' | 'uncertain' | 'completed' | 'refused' | 'authorized' | 'pending' | 'error';
    message: string;
    details?: string;
  };
  disabled: boolean;
  disabled_message: string;
  is_submit_button_visible: boolean;
  payment_error_messages_mapping?: PaymentErrorMessagesMapping;
}
export interface PaymentErrorMessagesMapping {
  do_not_honor: 'PAYMENT_ERROR_MESSAGE_DO_NOT_HONOR';
  insufficient_funds: 'PAYMENT_ERROR_MESSAGE_INSUFFICIENT_FUNDS';
  fraudulent: 'PAYMENT_ERROR_MESSAGE_FRAUD_SUSPECTED';
  processing_error: 'PAYMENT_ERROR_MESSAGE_TECHNICAL_FAILURE';
  expired_card: 'PAYMENT_ERROR_MESSAGE_EXPIRED_CARD';
  pickup_card: 'PAYMENT_ERROR_MESSAGE_CALL_ISSUER';
  card_velocity_exceeded: 'PAYMENT_ERROR_MESSAGE_ACTIVITY_LIMIT_EXCEEDED';
  lost_card: 'PAYMENT_ERROR_MESSAGE_CARD_STOLEN';
  authentication_required: 'PAYMENT_ERROR_MESSAGE_AUTHENTICATION_REQUIRED';
  generic_decline: 'PAYMENT_ERROR_MESSAGE_DECLINED';
  default: 'PAYMENT_ERROR_MESSAGE_DEFAULT';
}

export const PAYMENT_ERROR_MESSAGES = {
  PAYMENT_ERROR_MESSAGE_DO_NOT_HONOR: $localize`:@@payment_method_error.do_not_honor:Card issuer declined payment. Please contact bank or try another payment method.`,
  PAYMENT_ERROR_MESSAGE_INSUFFICIENT_FUNDS: $localize`:@@payment_method_error.insufficient_funds:Insufficient funds. Please check balance or try another payment method.`,
  PAYMENT_ERROR_MESSAGE_FRAUD_SUSPECTED: $localize`:@@payment_method_error.fraud_suspected:Bank blocked charge as it seemed unusual. Please contact bank or try another payment method.`,
  PAYMENT_ERROR_MESSAGE_TECHNICAL_FAILURE: $localize`:@@payment_method_error.technical_failure:Technical issue occurred. Please try again later or another payment method.`,
  PAYMENT_ERROR_MESSAGE_EXPIRED_CARD: $localize`:@@payment_method_error.expired_card:Card expired. Please update details or use another payment method.`,
  PAYMENT_ERROR_MESSAGE_CALL_ISSUER: $localize`:@@payment_method_error.call_issuer:Card issuer declined payment. Please contact bank or try another payment method.`,
  PAYMENT_ERROR_MESSAGE_ACTIVITY_LIMIT_EXCEEDED: $localize`:@@payment_method_error.activity_limit_exceeded:Card activity limit exceeded. Please contact bank or try another payment method.`,
  PAYMENT_ERROR_MESSAGE_CARD_STOLEN: $localize`:@@payment_method_error.card_stolen:Card reported lost or stolen. Please contact bank or use another payment method.`,
  PAYMENT_ERROR_MESSAGE_AUTHENTICATION_REQUIRED: $localize`:@@payment_method_error.authentication_required:Bank needs to verify it's you. Please follow their steps to confirm or try another method.`,
  PAYMENT_ERROR_MESSAGE_DECLINED: $localize`:@@payment_method_error.generic_decline:Card issuer declined payment. Please contact bank or try another payment method.`,
  PAYMENT_ERROR_MESSAGE_DEFAULT: $localize`:@@payment_method.error.generic:The payment could not be processed at this time. Please try again with another payment method.`,
};

export interface OrderformState {
  order: OrderformOrderState;
  paymentUrl: string;
  pending: boolean;
  submit: 'pending' | 'success' | 'failed' | 'initial';
  isExpressCheckoutClicked: boolean;
}

export interface OrderformVoucherValidateResponse {
  status: string;
  status_msg: string;
  discount_rate: number;
  discount_other_rates: number;
  discount_single_rate: number;
  discount_1st_amt: number;
  discount_oth_amts: number;
  discount_single_amt: number;
  code: string;
  is_voucher_code_input_locked: string;
  is_target_first_amount: string;
  target_first_amount: number;
  is_target_other_amounts: string;
  target_other_amounts: number;
  brutto_amount_discount: number;
  show_message: string;
}

export declare namespace FormFields {
  export interface DependsOn {
    // i_order_as: string;
    // country: string;

    [key: string]: string;
  }

  export interface SelectOption {
    [key: string]: string;
  }

  export interface Field {
    // fields identical with address and custom fields
    field_type?: string;
    type: string;
    postname: string;
    label: string;
    fieldname?: string;
    message_html?: string; // Custom Input (text)
    address_field?: any;
    is_used_in_eticket?: boolean;
    in_row_with_next?: boolean;
    max_value?: string | number;
    min_value?: number;
    maxlength?: string | number;
    minlength?: string | number;
    size?: number;
    rules?: string;
    id?: string;
    value?: string;
    input_position?: string;
    with_time?: boolean;
    skip_blacklist_check?: boolean;
    regex?: string;
    regex_message?: string;
    rows?: string;
    options?: false | Array<SelectOption | '-'>;
    past_dates_only?: boolean;
    future_date_only?: boolean;
    is_required?: boolean;
    is_checked?: boolean;
    is_readonly?: boolean;
    hint?: string;
    multi_select?: boolean;
    seperator?: string;

    // address interface comes only from address
    tooltip?: string;
    button?: string;
    theme?: string;
    default?: string;
    inline_label?: string;
    depends_on?: DependsOn;
    hidden_html?: string;
    autocomplete?: string;
    with_validation_icon?: boolean;
    hide?: boolean;
    placeholder?: string;
    css?: string;
    allow_null?: boolean;
    null_label?: string;
    onchange?: string;
    country_restriction_hint?: string;
    validators?: ValidatorFn[];
    asyncValidators?: AsyncValidatorFn[];
    'grid-col-width'?: number;
    is_latin_only?: boolean;
  }
}

export declare namespace OrderformSettingsStateCustomForms {
  export interface RootObject {
    no: number;
    headline: string;
    description: string;
    fields: FormFields.Field[];
    show_on: 'form' | 'product';
    show_on_products: number[];
  }
}

export interface CustomFormData {
  [postKeyFromBackend: string]: any; // CF-3533-6241-1-22: 'Hallo'
}

// POST Interfaces
export interface InterfacePost {
  splittest_data?: string;
  checkboxes?: CustomFormData;
  hidden_inputs?: CustomFormData;
  shipping_address?: CustomFormData;
  billing_address?: CustomFormData;
  has_billing_address?: number;
  //buyer?: CustomFormData;
  items?: {
    [product_id: number]: OrderformOrderProductItem;
  };
  payment_plan_id?: string;
  payment_plan_is_brutto_price_enforced?: 'Y' | 'N' | any;
  voucher_code?: string;
  voucher_code_added?: boolean;
  custom_forms?: CustomFormData;
  payment_provider?: OrderformPaymentProvider;
  payment_provider_meta?: CustomFormData;
  currency?: string;
  discount_display_position?: OrderformDiscountDisplayPosition;
  iframe_parent_url?: string;
  preselected_payment_plan_id?: string;
  selected_plan?: OrderformSelectedPlan;
  currency_code?: string;
  purchase_id?: string;
  is_plain_mode_enabled?: boolean;
  execute?: any; // TODO: define type
  // For new process API
  orderform_id?: number;
  main_product_id?: number;
  confirm_repurchase?: boolean;
  shipping_total?: CustomFormData;
  shipping_total_undiscounted?: CustomFormData;
  order_total_undiscounted?: CustomFormData;
  order_total?: CustomFormData;
  first_payment_at?: string;
  purchase_cause?: string;
  buy_url_id?: string | null;
  cart_id?: string | null;
  siteowner_id?: number;
  language?: string;
  affiliate_name?: string;
}

export type OrderformDiscountDisplayPosition = 'summary' | 'item' | 'summary,item';

export interface OrderformRedirectResponse {
  action: 'redirect' | 'show_message';
  headline: string;
  message: string;
  url: string;
  redirect_id?: string;
}

export interface CheckDoubleOrderResponse extends OrderformOrderStateMessageNotification {
  type: 'hidden' | 'lightbox';
  debug_info: string;
}

export type OrderformMessageType = 'success' | 'infos' | 'warnings' | 'errors';

export interface OrderFormValidationResult {
  valid: boolean;
  component: string;
  invalidFieldList: {
    id: string;
    errors: string[];
  }[];
}

export enum SiteOwners {
  DIGISTORE24_GMBH = 1,
  DIGISTORE24_INC = 2,
  DIGISTORE24_LTD = 3,
  DIGISTORE24_MSLW_LIMITED = 4,
}

export enum ClientActionType {
  REDIRECT_IFRAME = 'redirect_iframe',
  REDIRECT_FULL_PAGE = 'redirect_full_page',
  CLIENT_SDK_SUBMIT = 'client_sdk_submit',
}

export enum OrderStatus {
  VALIDATION_FAILED = 'validation_failed',
  PAYMENT_REQUESTED = 'payment_requested',
  PAID = 'paid',
  PAYMENT_FAILED = 'payment_failed',
  CREATION_FAILED = 'creation_failed',
  RISK_ANALYSIS_FAILED = 'risk_analysis_failed',
  COMPLETED = 'completed',
}

export interface OrderStatusResponse {
  order_id: string;
  status: string;
  validations: {
    order_data: string;
    billing_address: string;
    shipping_address: string;
    shipping_destination: string;
    vat: string;
    order_total: string;
    product_list: string;
    vouchers: string;
  };
  failure_reasons: {
    order_data: string | null;
    billing_address: string | null;
    shipping_address: string | null;
    shipping_destination: string | null;
    vat: string | null;
    order_total: string | null;
    product_list: string | null;
    vouchers: string | null;
  };
  failure_codes: {
    order_data: string | null;
    billing_address: string | null;
    shipping_address: string | null;
    shipping_destination: string | null;
    vat: string | null;
    order_total: string | null;
    product_list: string | null;
    vouchers: string | null;
  };
  client_action_payload: {
    redirect_url: string | null;
    action_type: ClientActionType | null;
    action_step: number | null;
  } | null;
  error: string;
  message: string;
}
